/**
 * Pure form-value → HubSpot-submission mapping + validation. No DOM, so it's
 * unit-testable. The DOM form (custom-form.ts) and the backend proxy both build
 * on these shapes.
 */
import type { SellerType } from './qualification.js'

export interface ILeadFormValues {
  sellerType: SellerType
  firstName: string
  lastName: string
  email: string
  phone: string
  storeName: string
  website: string
  brands: string[]
  usBased: boolean
  /**
   * One or more HubSpot option *values* (not labels) — see
   * `LEAD_CHANNEL_OPTIONS`. `;`-joined on submission, which is how HubSpot
   * expects a multiple-checkbox property.
   */
  leadChannel: string[]
  /**
   * UTM params resolved from the landing page's query string (see
   * `resolveUtmParams` in analytics.ts). Never user-entered; empty when the
   * landing URL had no such param.
   */
  utmSource: string
  utmMedium: string
  utmCampaign: string
  utmTerm: string
  utmContent: string
}

/**
 * "How did you hear about us?" — the form's last question, multi-select.
 *
 * Labels are what the lead sees; values are the option values on the Contact's
 * `inbound_lead_channel` ("Lead Channel") property. The two differ deliberately:
 * the property carries ~27 options including internal-only ones (BDR Outbound,
 * Internal Referral, specific brand names), and this is the customer-facing
 * subset. Two labels also merge a pair of options into one choice — keep them
 * paired here rather than deriving one from the other.
 *
 * The form renders these in a random order on each load (see custom-form.ts),
 * so nothing here should depend on the array order except `Other`, which is
 * pinned last as the catch-all.
 */
export const LEAD_CHANNEL_OPTIONS: { label: string; value: string }[] = [
  { label: 'Facebook/Instagram', value: 'Facebook' },
  { label: 'Tradeshow', value: 'Tradeshow' },
  { label: 'Podcast', value: 'Podcast' },
  { label: 'YouTube', value: 'YouTube' },
  { label: 'Another Retailer/Brand', value: 'Another Retailer' },
  { label: 'Boutique Hub', value: 'Boutique Hub' },
  { label: 'Management One', value: 'Management One' },
  { label: 'Online Search', value: 'Google' },
  { label: 'Other', value: 'Website' },
]

/** The catch-all, pinned last however the rest are shuffled. */
export const LEAD_CHANNEL_OTHER_LABEL = 'Other'

/**
 * Query param that presets the "how did you hear about us?" answer, for a
 * landing page whose traffic source is already known (e.g. a Meta ad pointing
 * at `?lead_channel=Meta%20(Direct)`). When it resolves to a known option the
 * question is hidden and the value is submitted as-is.
 */
export const LEAD_CHANNEL_PARAM = 'lead_channel'

/**
 * Every option value on the Contact's `inbound_lead_channel` property, read
 * from portal 19540235 — a superset of `LEAD_CHANNEL_OPTIONS`, since a preset
 * channel may be an internal-only one (e.g. `Referral Rock`). HubSpot rejects
 * the whole submission on an unknown option, so the param is checked against
 * this list; a value added in HubSpot but not here just leaves the question
 * visible rather than breaking the form.
 */
export const LEAD_CHANNEL_VALUES: readonly string[] = [
  'AI Search / Chat',
  'Another Brand',
  'Another Retailer',
  'BDR Outbound',
  'Boutique Hub',
  'Email',
  'Enza Costa',
  'Facebook',
  'Google',
  'Google (Direct)',
  'Inbound via Sales',
  'Inbound via Support',
  'Industry Partner',
  'Industry Partner (Direct)',
  'Instagram',
  'Internal Referral',
  'Linkedin',
  'Magnolia Pearl',
  'Management One',
  'Meta (Direct)',
  'Neon Buddha',
  'One of My Brands',
  'Payout Calculator',
  'Podcast',
  'Prospect HVT Drip Emails',
  'Referral Rock',
  'Shoe Dogs United',
  'Shopify',
  'Snagged & Bagged',
  'Tradeshow',
  'Tradeshow (Direct)',
  'Website',
  'WhizBang',
  'YouTube',
]

/**
 * Resolve a `lead_channel` query param to its canonical HubSpot option value,
 * or '' when absent or unknown. Case-insensitive, so a hand-typed
 * `?lead_channel=referral%20rock` still matches `Referral Rock`.
 */
export const resolveLeadChannelParam = (raw?: string | null): string => {
  const wanted = raw?.trim().toLowerCase() ?? ''
  if (!wanted) {
    return ''
  }
  return LEAD_CHANNEL_VALUES.find((v) => v.toLowerCase() === wanted) ?? ''
}

/**
 * HubSpot object type ids used by the Forms Submission API to route a field to
 * the contact (`0-1`) or the company (`0-2`) created by the submission. HubSpot
 * associates the two when they come from the same submission.
 */
export const HS_CONTACT = '0-1'
export const HS_COMPANY = '0-2'
export type HsObjectTypeId = typeof HS_CONTACT | typeof HS_COMPANY

export interface IHubspotField {
  objectTypeId: HsObjectTypeId
  name: string
  value: string
}

export interface IFieldMapping {
  /** HubSpot property internal name. */
  name: string
  /** Which object the property lives on. */
  objectTypeId: HsObjectTypeId
}

/**
 * Maps our field keys to HubSpot properties and the object each lives on.
 * Override per portal if the form uses different names.
 *
 * `storeName` maps to the Company object's built-in `name` property so the
 * store name becomes the company's name. (HubSpot still auto-creates and
 * associates the company from the contact's email domain; a staging test showed
 * the contact-level `company` field does NOT set the company object's name,
 * which is why store name is written to the Company here.) The self-reported
 * brand list and priority-brand count are also written to the Company; the
 * person's details to the Contact. The mapping must match how the fields are
 * configured on the HubSpot form.
 *
 * `sellerType` (retailer vs brand) goes to the Contact's built-in `contact_type`
 * enumeration — the submit Lambda reads that same field back to compute the
 * qualification outcome, so keep `SELLER_TYPE_FIELD_NAME` in `submit.ts` in sync.
 */
export type FieldMap = Record<keyof ILeadFormValues, IFieldMapping>

export const DEFAULT_FIELD_MAP: FieldMap = {
  sellerType: { name: 'contact_type', objectTypeId: HS_CONTACT },
  firstName: { name: 'firstname', objectTypeId: HS_CONTACT },
  lastName: { name: 'lastname', objectTypeId: HS_CONTACT },
  email: { name: 'email', objectTypeId: HS_CONTACT },
  phone: { name: 'mobilephone', objectTypeId: HS_CONTACT },
  storeName: { name: 'name', objectTypeId: HS_COMPANY },
  website: { name: 'website', objectTypeId: HS_CONTACT },
  brands: { name: 'self_reported_brands', objectTypeId: HS_COMPANY },
  usBased: { name: 'based_in_us', objectTypeId: HS_COMPANY },
  leadChannel: { name: 'inbound_lead_channel', objectTypeId: HS_CONTACT },
  // All five on the Contact — separate from the pre-existing Company-level
  // utm_source/medium/campaign written by packages/hubspot-sdk's registration
  // flow, which is an unrelated integration.
  utmSource: { name: 'utm_source', objectTypeId: HS_CONTACT },
  utmMedium: { name: 'utm_medium', objectTypeId: HS_CONTACT },
  utmCampaign: { name: 'utm_campaign', objectTypeId: HS_CONTACT },
  utmTerm: { name: 'utm_term', objectTypeId: HS_CONTACT },
  utmContent: { name: 'utm_content', objectTypeId: HS_CONTACT },
}

/**
 * `contact_type` is an enumeration on the Contact; its option values are
 * capitalized ("Retailer" / "Brand"), unlike our internal seller-type values.
 */
export const CONTACT_TYPE_VALUE: Record<SellerType, string> = {
  retailer: 'Retailer',
  brand: 'Brand',
}

// eslint-disable-next-line prefer-named-capture-group
const EMAIL_RE = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/

/** Digits, plus the separators a US number is commonly typed with. */
const PHONE_ALLOWED_RE = /^[\d\s()+-]+$/
const PHONE_SEPARATOR_RE = /[\s()-]/g
/** Optional `+1`/`1` country code, then exactly ten digits. */
// eslint-disable-next-line prefer-named-capture-group
const PHONE_SHAPE_RE = /^(?:\+?1)?(\d{10})$/
/** NANP: neither the area code nor the exchange code may start with 0 or 1. */
const PHONE_NANP_RE = /^[2-9]\d{2}[2-9]\d{6}$/
/** Every digit the same — 000-000-0000 and friends. */
const PHONE_REPEATED_RE = /^(\d)\1{9}$/

/**
 * US phone validation.
 *
 * Ten digits, optionally prefixed with a `+1` country code, written with any mix
 * of spaces, parentheses and hyphens. Letters (and any other punctuation) are
 * rejected outright rather than stripped, so `555-CALL-NOW` fails instead of
 * silently becoming a different number.
 *
 * Placeholders are rejected via two NANP rules — an area code or exchange code
 * starting with 0 or 1 is not assignable, which covers `000-000-0000`,
 * `111-111-1111` and `123-456-7890` — plus an explicit repeated-digit check for
 * the ones those rules let through, like `222-222-2222`.
 */
export const isValidUsPhone = (raw: string): boolean =>
  usPhoneDigits(raw) !== ''

/**
 * The ten national digits of a valid US number, or '' if it isn't one. Single
 * source of truth for the rules above — validation and normalization must never
 * disagree about what counts as valid.
 */
const usPhoneDigits = (raw: string): string => {
  const value = raw.trim()
  if (!PHONE_ALLOWED_RE.test(value)) {
    return ''
  }
  // A `+` survives here, so it can only be validated in leading position.
  const shape = PHONE_SHAPE_RE.exec(value.replace(PHONE_SEPARATOR_RE, ''))
  if (!shape) {
    return ''
  }
  const digits = shape[1]
  return PHONE_NANP_RE.test(digits) && !PHONE_REPEATED_RE.test(digits)
    ? digits
    : ''
}

/**
 * E.164 form (`+15551234567`), or '' if the input isn't a valid US number.
 *
 * Ad platforms match on a canonical format, so a number stored as typed
 * ("(415) 555-0100") has to be normalized before it's hashed — otherwise the
 * same person hashes to a different value each time the formatting differs.
 */
export const normalizeUsPhoneE164 = (raw: string): string => {
  const digits = usPhoneDigits(raw)
  return digits === '' ? '' : `+1${digits}`
}

/**
 * Lower-cased and trimmed, which is what the ad platforms hash. Deliberately
 * does no further canonicalization (no gmail dot/plus stripping) — that changes
 * which mailbox the address denotes and platforms don't expect it.
 */
export const normalizeEmail = (raw: string): string => raw.trim().toLowerCase()

/** Returns a map of field key -> error message; empty object means valid. */
export const validateLeadForm = (
  values: ILeadFormValues
): Partial<Record<keyof ILeadFormValues, string>> => {
  const errors: Partial<Record<keyof ILeadFormValues, string>> = {}
  if (!values.firstName.trim()) {
    errors.firstName = 'First name is required'
  }
  if (!EMAIL_RE.test(values.email.trim())) {
    errors.email = 'A valid email is required'
  }
  if (!values.phone.trim()) {
    errors.phone = 'Phone number is required'
  } else if (!isValidUsPhone(values.phone)) {
    errors.phone = 'Enter a valid 10-digit US phone number'
  }
  if (!values.storeName.trim()) {
    errors.storeName = 'Store name is required'
  }
  if (values.leadChannel.length === 0) {
    errors.leadChannel = 'Please tell us how you heard about us'
  }
  return errors
}

/**
 * Build the HubSpot Submission API `fields` array (each tagged with its
 * objectTypeId). Empty optional fields (last name, website, brands) are
 * omitted; `usBased` is a stringified boolean. For retailers, `brands` is
 * `;`-joined; for brand-type sellers the form doesn't collect a brand list at
 * all, so `self_reported_brands` is populated from `storeName` instead.
 */
export const buildSubmissionFields = (
  values: ILeadFormValues,
  map: FieldMap = DEFAULT_FIELD_MAP
): IHubspotField[] => {
  const field = (m: IFieldMapping, value: string): IHubspotField => {
    return { objectTypeId: m.objectTypeId, name: m.name, value }
  }
  const fields: IHubspotField[] = [
    // eslint-disable-next-line security/detect-object-injection
    field(map.sellerType, CONTACT_TYPE_VALUE[values.sellerType]),
    field(map.firstName, values.firstName.trim()),
    field(map.email, values.email.trim()),
    field(map.phone, values.phone.trim()),
    field(map.storeName, values.storeName.trim()),
    field(map.usBased, String(values.usBased)),
    // `;`-joined, same as brands — HubSpot's separator for multi-value options.
    field(map.leadChannel, values.leadChannel.join(';')),
  ]
  if (values.lastName.trim()) {
    fields.push(field(map.lastName, values.lastName.trim()))
  }
  if (values.website.trim()) {
    fields.push(field(map.website, values.website.trim()))
  }
  if (values.sellerType === 'brand') {
    // Brand-type sellers no longer answer "which brands do you carry?" — their
    // own business name already is the answer, so self_reported_brands is
    // populated from storeName instead of a brand-list the form no longer asks.
    if (values.storeName.trim()) {
      fields.push(field(map.brands, values.storeName.trim()))
    }
  } else if (values.brands.length > 0) {
    fields.push(field(map.brands, values.brands.join(';')))
  }
  if (values.utmSource.trim()) {
    fields.push(field(map.utmSource, values.utmSource.trim()))
  }
  if (values.utmMedium.trim()) {
    fields.push(field(map.utmMedium, values.utmMedium.trim()))
  }
  if (values.utmCampaign.trim()) {
    fields.push(field(map.utmCampaign, values.utmCampaign.trim()))
  }
  if (values.utmTerm.trim()) {
    fields.push(field(map.utmTerm, values.utmTerm.trim()))
  }
  if (values.utmContent.trim()) {
    fields.push(field(map.utmContent, values.utmContent.trim()))
  }
  return fields
}
